import React, { useState, useEffect } from 'react';
import { Search, Plus, Edit2, Trash2, Bot, Power } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ChatInterface from '../../components/ChatInterface';
import AssistantNew from './AssistantNew';

interface Assistant {
  id: string;
  nombre: string;
  descripcion: string;
  modelo: string;
  estado: 'activo' | 'inactivo';
  api_configuracion_id: string | null;
  assistant_id: string | null;
  temperatura: number;
  max_tokens: number;
  top_p: number;
  frequency_penalty: number;
  presence_penalty: number;
  stop_sequences: string[];
  tools: any[];
  instructions: string;
  openai_assistant_id?: string;
  avatar_url?: string;
}

export default function Assistants() {
  const [assistants, setAssistants] = useState<Assistant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedAssistant, setSelectedAssistant] = useState<Assistant | null>(null);
  const [showNewAssistant, setShowNewAssistant] = useState(false);
  const [editingAssistant, setEditingAssistant] = useState<Assistant | null>(null);

  useEffect(() => {
    fetchAssistants();
  }, []);

  const fetchAssistants = async () => {
    try {
      setIsLoading(true);
      setError('');

      const { data: assistantsData, error } = await supabase
        .from('asistentes')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAssistants(assistantsData || []);

    } catch (error: any) {
      console.error('Error fetching assistants:', error);
      setError('Error al cargar los asistentes');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async (assistant: Assistant) => {
    try {
      const newStatus = assistant.estado === 'activo' ? 'inactivo' : 'activo';
      const { error } = await supabase
        .from('asistentes')
        .update({ estado: newStatus })
        .eq('id', assistant.id);

      if (error) throw error;
      setSuccess(`Asistente ${newStatus === 'activo' ? 'activado' : 'desactivado'} correctamente`);
      fetchAssistants();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('¿Estás seguro de eliminar este asistente? Esta acción no se puede deshacer.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('asistentes')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setSuccess('Asistente eliminado correctamente');
      fetchAssistants();
    } catch (error: any) {
      setError(error.message);
    }
  };

  const handleChat = (assistant: Assistant) => {
    setSelectedAssistant(assistant);
    setShowChatModal(true);
  };

  const handleEdit = (assistant: Assistant) => {
    setEditingAssistant(assistant);
    setShowNewAssistant(true);
  };

  const filteredAssistants = assistants.filter(assistant =>
    assistant.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assistant.descripcion.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assistant.modelo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (showNewAssistant) {
    return (
      <AssistantNew 
        onSuccess={() => {
          setShowNewAssistant(false);
          setEditingAssistant(null);
          fetchAssistants();
        }}
        onError={(error) => setError(error)}
        editingAssistant={editingAssistant}
      />
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Gestión de Asistentes</h1>
        <button 
          onClick={() => {
            setEditingAssistant(null);
            setShowNewAssistant(true);
          }}
          className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          <Plus className="w-5 h-5 mr-2" />
          Añadir Asistente
        </button>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
          {success}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-md">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Buscar asistentes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAssistants.map((assistant) => (
              <div key={assistant.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden">
                <div className="relative">
                  <img
                    src={assistant.avatar_url || 'https://images.unsplash.com/photo-1464582883107-8adf2dca8a9f?w=400&auto=format&fit=crop&q=80'}
                    alt={assistant.nombre}
                    className="w-full h-48 object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                  <div className="absolute top-2 right-2 flex space-x-1">
                    <button
                      onClick={() => handleUpdateStatus(assistant)}
                      className={`p-1 rounded-full ${
                        assistant.estado === 'activo'
                          ? 'bg-green-100 text-green-600'
                          : 'bg-red-100 text-red-600'
                      }`}
                      title={assistant.estado === 'activo' ? 'Desactivar' : 'Activar'}
                    >
                      <Power className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleEdit(assistant)}
                      className="p-1 bg-blue-100 text-blue-600 rounded-full"
                      title="Editar"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(assistant.id)}
                      className="p-1 bg-red-100 text-red-600 rounded-full"
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">{assistant.nombre}</h3>
                  <p className="text-sm text-gray-500 mb-3">{assistant.modelo}</p>
                  <p className="text-gray-600 text-sm line-clamp-3">{assistant.descripcion}</p>
                  <button
                    onClick={() => handleChat(assistant)}
                    className="mt-4 w-full flex items-center justify-center px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Bot className="w-5 h-5 mr-2" />
                    Iniciar Chat
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chat Modal */}
      {showChatModal && selectedAssistant && (
        <ChatInterface 
          onClose={() => {
            setShowChatModal(false);
            setSelectedAssistant(null);
          }}
          selectedAssistant={selectedAssistant}
        />
      )}
    </div>
  );
}